import React, { useCallback, useEffect, useRef, useState } from 'react';
import store from './journalStore';

/* 🔔 Nudges — little real-time pings between us. Tap a button, the other
   phone buzzes. Pushes are payload-less (the notification just says "tap to
   see"); the actual nudge shows here in the feed when the app opens. */

const PRESETS = [
  { kind: 'love', emoji: '💗', text: 'love you' },
  { kind: 'miss', emoji: '🥺', text: 'miss you' },
  { kind: 'think', emoji: '🫶', text: 'thinking of you' },
  { kind: 'call', emoji: '📞', text: 'call me?' },
  { kind: 'kiss', emoji: '💋', text: 'mwah' },
  { kind: 'home', emoji: '🏡', text: 'come home' },
];

const supported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

const urlB64ToBytes = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const base = (b64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  const d = Math.round(s / 86400);
  return d === 1 ? 'yesterday' : `${d}d ago`;
};

// Exact local date + time, e.g. "18 Jun 2026, 1:50 PM" — so we always know
// precisely when a nudge landed, not just "2h ago".
const stamp = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export default function Nudges({ passcode, me, onPickMe }) {
  const isPerson = me === 'Tanush' || me === 'Anusha';
  const other = me === 'Anusha' ? 'Tanush' : 'Anusha';
  const [feed, setFeed] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [pushState, setPushState] = useState('unknown'); // unknown|unsupported|off|on|denied|busy
  const [custom, setCustom] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [toast, setToast] = useState('');
  const [sending, setSending] = useState('');
  const seenSent = useRef(false);

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 2200);
  };

  const loadFeed = useCallback(async () => {
    if (!isPerson) return;
    try {
      const { nudges, unseen: u } = await store.nudges(passcode, me, 'all');
      setFeed(nudges);
      setUnseen(u);
      if (u > 0 && !seenSent.current) {
        seenSent.current = true;
        store.seenNudges(passcode, me).catch(() => {});
      }
    } catch (e) {
      /* ignore */
    }
  }, [passcode, me, isPerson]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  // reflect current push subscription state
  useEffect(() => {
    if (!supported()) {
      setPushState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setPushState('denied');
      return;
    }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setPushState(sub ? 'on' : 'off');
      } catch (e) {
        setPushState('off');
      }
    })();
  }, []);

  const enablePush = async () => {
    setPushState('busy');
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPushState(perm === 'denied' ? 'denied' : 'off');
        return;
      }
      const pub = await store.vapidPublic(passcode);
      if (!pub) {
        showToast('Push needs the live site 💕');
        setPushState('off');
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToBytes(pub),
      });
      await store.subscribePush(passcode, sub.toJSON(), me);
      setPushState('on');
      showToast('Nudges on — your phone will buzz now 🔔');
    } catch (e) {
      setPushState('off');
      showToast('Could not turn on nudges 💔');
    }
  };

  const send = async (p) => {
    setSending(p.kind);
    try {
      const r = await store.sendNudge(passcode, { from: me, kind: p.kind, text: p.text, emoji: p.emoji });
      const n = r && r.devices;
      showToast(n ? `sent ${p.emoji} — buzzed ${other} 📲` : `sent ${p.emoji} (${other} hasn’t turned on nudges yet)`);
    } catch (e) {
      showToast('Could not send 💔');
    } finally {
      setSending('');
    }
  };

  const sendCustom = async (e) => {
    e.preventDefault();
    const text = custom.trim();
    if (!text) return;
    await send({ kind: 'custom', emoji: '💌', text });
    setCustom('');
    setShowCustom(false);
  };

  if (!isPerson) {
    return (
      <div className="aj-nudges aj-nudges-pick">
        <span className="aj-nudge-title">who’s holding this phone?</span>
        <span className="aj-muted aj-nudge-hint">so your nudges land on the right side of us 💕</span>
        <div className="aj-nudge-pickbtns">
          <button type="button" className="aj-chip" onClick={() => onPickMe && onPickMe('Tanush')}>I’m Tanush</button>
          <button type="button" className="aj-chip" onClick={() => onPickMe && onPickMe('Anusha')}>I’m Anusha</button>
        </div>
      </div>
    );
  }

  return (
    <div className="aj-nudges">
      <div className="aj-nudge-head">
        <span className="aj-nudge-title">send {other} a little something</span>
        {pushState === 'on' && <span className="aj-nudge-onpill">🔔 on</span>}
      </div>

      <div className="aj-nudge-btns">
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p.kind}
            className={`aj-nudge-btn${sending === p.kind ? ' is-sending' : ''}`}
            onClick={() => send(p)}
            disabled={!!sending}
          >
            <span className="aj-nudge-emoji" aria-hidden="true">{p.emoji}</span>
            <span className="aj-nudge-label">{p.text}</span>
          </button>
        ))}
        <button type="button" className="aj-nudge-btn aj-nudge-custom" onClick={() => setShowCustom((s) => !s)}>
          <span className="aj-nudge-emoji" aria-hidden="true">✍️</span>
          <span className="aj-nudge-label">your words</span>
        </button>
      </div>

      {showCustom && (
        <form className="aj-nudge-customform" onSubmit={sendCustom}>
          <input
            className="aj-input"
            value={custom}
            maxLength={200}
            autoFocus
            onChange={(e) => setCustom(e.target.value)}
            placeholder={`say anything to ${other}… 💭`}
          />
          <button type="submit" className="aj-btn aj-btn-sm" disabled={!custom.trim()}>send 💌</button>
        </form>
      )}

      {pushState === 'off' && (
        <button type="button" className="aj-nudge-enable" onClick={enablePush}>
          🔔 turn on nudges on this phone
        </button>
      )}
      {pushState === 'busy' && <div className="aj-muted aj-nudge-hint">turning on…</div>}
      {pushState === 'denied' && (
        <div className="aj-muted aj-nudge-hint">
          notifications are blocked for this site — flip them on in your browser settings to get buzzed. 💕
        </div>
      )}
      {pushState === 'unsupported' && (
        <div className="aj-muted aj-nudge-hint">
          to get buzzed: add this page to your Home Screen first, then open it from there. 📲
        </div>
      )}

      {feed.length > 0 && (
        <div className="aj-nudge-feed">
          <div className="aj-nudge-feed-head">
            you &amp; {other} {unseen > 0 ? <span className="aj-nudge-unseen">{unseen} new</span> : null}
          </div>
          {feed.slice(0, 20).map((n) => {
            const sent = n.from === me;
            return (
              <div
                className={`aj-nudge-item${sent ? ' is-sent' : ' is-received'}${!sent && !n.seenAt ? ' is-new' : ''}`}
                key={n.id}
              >
                <span className="aj-nudge-item-emoji" aria-hidden="true">{n.emoji}</span>
                <span className="aj-nudge-item-body">
                  <span className="aj-nudge-item-text">{n.text || n.kind}</span>
                  <span className="aj-nudge-item-meta">
                    {sent ? `you → ${other}` : `${other} → you`} · {stamp(n.createdAt)}
                  </span>
                </span>
                <span className="aj-nudge-item-time" title={stamp(n.createdAt)}>{ago(n.createdAt)}</span>
              </div>
            );
          })}
        </div>
      )}

      {toast && <div className="aj-ss-toast">{toast}</div>}
    </div>
  );
}
